import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Capacitor } from "@capacitor/core";
import { firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { AppService } from "./app.service";
import { DevModeService } from "./dev-mode.service";
import { DiagnosticsConsentService } from "./diagnostics-consent.service";
import { LogClassifierService } from "./log-classifier.service";
import { LocalStorageService } from "./localStorage.service";
import { LogEntry, LogService } from "./log.service";
import { scrubLogText } from "./log-scrubber";

export interface SendResult {
  ok: boolean;
  status: number;
  reason: "sent" | "empty" | "network" | "unauthenticated" | "rate-limited" | "server-error" | "client-error";
}

interface ReportPayload {
  source: "mobile";
  trigger: "manual" | "error" | "batch";
  context: {
    appVersion?: string;
    platform: string;
    userAgent: string;
    userId?: number;
    role?: string;
    route?: string;
    note?: string;
  };
  entries: Array<{
    ts: number;
    level: string;
    message: string;
  }>;
}

/**
 * Pushes scrubbed log batches to the backend for AI analysis.
 *
 * Triggers:
 *   - manual: user taps "Envoyer au support" in LogViewer
 *   - error:  auto-debounced send when a window.error fires (or any log
 *             of level=error is appended). One send per 60s max.
 *   - batch:  periodic flush (only when errors are pending)
 *
 * RGPD: payloads pass through scrubLogText() — bearer tokens, emails,
 * phone numbers, GPS precision are masked client-side BEFORE upload.
 */
@Injectable({ providedIn: "root" })
export class LogReporterService {
  private static readonly ENDPOINT = "/frontoffice/diagnostics/logs";
  private static readonly MIN_INTERVAL_MS = 60_000; // throttle auto-sends
  private static readonly MAX_ENTRIES_PER_PUSH = 300;

  private lastAutoSendAt = 0;
  private pendingErrorSend = false;
  private initialized = false;
  private autoSendAllowed = false;
  private autoSendAllowedSince = 0;
  /** Cached app version, hydrated once at init() from assets/settings/version.json. */
  private appVersion: string | undefined;

  /** Error signatures seen during the current debounce window. */
  private pendingSignatures = new Set<string>();

  constructor(
    private http: HttpClient,
    private logService: LogService,
    private localStorageService: LocalStorageService,
    private devModeService: DevModeService,
    private consentService: DiagnosticsConsentService,
    private appService: AppService,
    private router: Router,
    private classifier: LogClassifierService,
  ) { }

  /**
   * Wire auto-send on error. Idempotent. Called from AppComponent boot.
   *
   * Auto-send policy:
   *   - PRODUCTION: gated by user consent (DiagnosticsConsentService).
   *   - DEV / staging: unconditional (not store-distributed).
   *
   * Volume control: every candidate buffer goes through the on-device
   * LogClassifierService FIRST. Noise, known-benign errors, and duplicate
   * signatures are dropped on the device — they never reach the network.
   * This is what keeps upload volume low instead of flooding the backend.
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Hydrate the app version once so buildContext() can attach it sync.
    // Fire-and-forget — if the JSON 404s in dev, context just omits the field.
    this.appService.getBuildVersion().subscribe({
      next: (v: any) => {
        if (v?.version) {
          this.appVersion = String(v.version).slice(0, 32);
        }
      },
      error: () => { /* ignore */ },
    });

    // Track effective consent state.
    this.consentService.autoSendAllowed$.subscribe((s) => {
      const wasAllowed = this.autoSendAllowed;
      this.autoSendAllowed = s.allowed;
      if (this.autoSendAllowed && !wasAllowed) {
        this.autoSendAllowedSince = Date.now();
        this.pendingSignatures.clear();
      }
      if (!this.autoSendAllowed) {
        this.autoSendAllowedSince = 0;
        this.pendingSignatures.clear();
      }
    });

    // Subscribe to errors; classify on-device before scheduling any upload.
    this.logService.entries$.subscribe((entries) => {
      if (!entries.length) return;
      const last = entries[entries.length - 1];
      if (last.level !== "error") return;
      if (!this.isAutoSendAllowed()) {
        this.pendingSignatures.clear();
        return;
      }
      // On-device filter — drops noise / benign / duplicates before network.
      const decision = this.classifier.classify(entries);
      if (!decision.upload) return;
      if (decision.signature) {
        this.pendingSignatures.add(decision.signature);
      }
      this.scheduleErrorSend();
    });
  }

  /** Reflects the effective gate at this instant. */
  isAutoSendAllowed(): boolean {
    if (!this.isAuthenticated()) {
      return false;
    }
    if (!environment.production) return true;
    return this.autoSendAllowed;
  }

  /**
   * User-initiated full dump. Returns a detailed result so callers can
   * differentiate offline / not-authenticated / server-error cases.
   */
  async sendManual(note?: string): Promise<SendResult> {
    return this.send("manual", note);
  }

  /** Auto-send debounce: collapses bursts into one push per MIN_INTERVAL_MS. */
  private scheduleErrorSend(): void {
    if (this.pendingErrorSend) return;
    const now = Date.now();
    const wait = Math.max(0, LogReporterService.MIN_INTERVAL_MS - (now - this.lastAutoSendAt));
    this.pendingErrorSend = true;
    setTimeout(async () => {
      this.pendingErrorSend = false;
      // Re-check the gate INSIDE the timeout callback: the user may have
      // revoked consent during the 60s debounce window, in which case the
      // initial schedule decision is stale and we must NOT upload.
      if (!this.isAutoSendAllowed()) {
        this.pendingSignatures.clear();
        return;
      }
      this.lastAutoSendAt = Date.now();
      const signatures = Array.from(this.pendingSignatures);
      this.pendingSignatures = new Set<string>();
      this.classifier.recordUploadAttempt();
      try {
        const result = await this.send("error");
        // On success, record the signature so the SAME error doesn't get
        // re-uploaded for the next DEDUP_TTL window.
        if (result.ok) {
          this.classifier.recordUploadedMany(signatures);
        }
      } catch (_) {
        // Network errors during a log push must NEVER break the app or
        // be re-logged (would loop). Silent for auto-sends only.
      }
    }, wait);
  }

  private async send(trigger: "manual" | "error" | "batch", note?: string): Promise<SendResult> {
    if (!this.isAuthenticated()) {
      return { ok: false, status: 0, reason: "unauthenticated" };
    }
    const entries = this.getEntriesForTrigger(trigger);
    if (entries.length === 0) {
      return { ok: false, status: 0, reason: "empty" };
    }
    const slice = entries.slice(-LogReporterService.MAX_ENTRIES_PER_PUSH);
    const payload: ReportPayload = {
      source: "mobile",
      trigger,
      context: this.buildContext(note),
      entries: slice.map((e: LogEntry) => ({
        ts: e.timestamp,
        level: e.level,
        message: scrubLogText(e.message),
      })),
    };
    try {
      await firstValueFrom(
        this.http.post(`${environment.authUrl}${LogReporterService.ENDPOINT}`, payload)
      );
      return { ok: true, status: 201, reason: "sent" };
    } catch (err) {
      const httpErr = err as HttpErrorResponse;
      const status = typeof httpErr?.status === "number" ? httpErr.status : 0;
      const reason: SendResult["reason"] =
        status === 0 || status === undefined
          ? "network"
          : status === 401 || status === 403
            ? "unauthenticated"
            : status === 429
              ? "rate-limited"
              : status >= 500
                ? "server-error"
                : "client-error";
      return { ok: false, status, reason };
    }
  }

  private buildContext(note?: string): ReportPayload["context"] {
    const user = this.safeGetUser();
    // Resolve role from multiple shapes observed in localStorage (`roles[]`
    // for newer users, plain `role` string for legacy ones).
    const rawRole =
      user?.client?.roles?.[0] ??
      user?.client?.role ??
      user?.role ??
      undefined;
    const role = this.normalizeRole(rawRole);
    // Capacitor.getPlatform() returns "ios" | "android" | "web" — much more
    // useful than the deprecated `navigator.platform` (which returns
    // "MacIntel" or "Linux armv8l" generic strings).
    let platform = "web";
    try {
      platform = Capacitor.getPlatform() || "web";
    } catch (_) { /* SSR / unit tests */ }
    // Angular Router holds the *real* SPA route. `window.location.pathname`
    // is almost always "/" or "/index.html" in a Capacitor WebView — it
    // would never reflect "/prestataire/demande-list" / etc.
    const rawRoute = (this.router?.url ?? window?.location?.pathname) || undefined;
    const route = rawRoute ? scrubLogText(rawRoute).slice(0, 512) : undefined;
    return {
      appVersion: this.appVersion,
      platform,
      userAgent: navigator?.userAgent || "unknown",
      userId: user?.client?.id ?? user?.id,
      role,
      route,
      note: note ? scrubLogText(note).slice(0, 500) : undefined,
    };
  }

  private isAuthenticated(): boolean {
    return this.localStorageService.isAuth();
  }

  private safeGetUser(): any {
    try {
      return this.localStorageService.getUserData();
    } catch (_) {
      return null;
    }
  }

  private normalizeRole(role: any): string | undefined {
    const raw = typeof role === "string" ? role : role?.name;
    const normalized = String(raw ?? "").trim().toLowerCase();
    return normalized ? normalized.slice(0, 32) : undefined;
  }

  private getEntriesForTrigger(trigger: "manual" | "error" | "batch"): LogEntry[] {
    const entries = this.logService.getEntries();
    if (
      trigger !== "error" ||
      !environment.production ||
      this.autoSendAllowedSince <= 0
    ) {
      return entries;
    }
    return entries.filter((entry) => entry.timestamp >= this.autoSendAllowedSince);
  }
}
